import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const Section = ({ label, body, color }) => {
  if (!body) return null;
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: '.08em', color }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{body}</Typography>
    </Box>
  );
};

/**
 * Check if a URL is a Confluence page URL.
 */
const isConfluenceUrl = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.pathname.includes('/wiki/');
  } catch {
    return false;
  }
};

/**
 * The rabbit holes and no-gos from this bet's pitch, next to the hill.
 *
 * This is where they earn their keep. Cutting scope in week five is a hard
 * conversation if the boundaries live in a document nobody has open; it is an easy
 * one if the team can point at what they already agreed to leave out.
 */
const BetGuardrails = ({ pitch, pitchUrl, onSanityCheck }) => {
  const [open, setOpen] = useState(false);

  const hasExternalPitch = pitchUrl && isConfluenceUrl(pitchUrl);

  // No pitch at all (internal or external) - nothing to show
  if (!pitch && !hasExternalPitch) return null;

  // External Confluence pitch only - show link and sanity check button
  if (!pitch && hasExternalPitch) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Link
            href={pitchUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            color="text.secondary"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            External Pitch (Confluence)
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
          {onSanityCheck && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={onSanityCheck}
              sx={{ flexShrink: 0 }}
            >
              Sanity Check
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  const hasGuardrailsContent = pitch.rabbitHoles || pitch.noGos || pitch.solution;

  // Pitch exists but no guardrails content - show just the sanity check button
  if (!hasGuardrailsContent) {
    if (!onSanityCheck) return null;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Pitch: {pitch.title}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onSanityCheck}
            sx={{ flexShrink: 0 }}
          >
            Sanity Check
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', flexGrow: 1 }}
          onClick={() => setOpen(current => !current)}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: '.08em', color: 'text.secondary' }}
          >
            WHAT WE SAID WE WOULD NOT DO
          </Typography>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
        {onSanityCheck && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onSanityCheck}
            sx={{ flexShrink: 0 }}
          >
            Sanity Check
          </Button>
        )}
      </Box>

      {!open && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          From the pitch for {pitch.title}. Worth rereading before cutting anything.
        </Typography>
      )}

      <Collapse in={open}>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <Section label="RABBIT HOLES" body={pitch.rabbitHoles} color="warning.main" />
          <Section label="NO-GOS" body={pitch.noGos} color="error.main" />
          <Section label="THE SHAPE" body={pitch.solution} color="text.secondary" />
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default BetGuardrails;
